// wizardValidation.ts - Validation functions for ProductCreationWizard
import type {
  BasicInfoData,
  ClassificationData,
  SalesInfoData,
  ShippingData
} from "../components/WizardSteps"
import type { ProductData } from "../components/ProductCreationWizard/ProductCreationWizard"

// Basic Info validation (for step progression)
export function validateBasicInfo(data: BasicInfoData): string[] {
  const errors: string[] = []
  if (!data.productSku?.trim()) errors.push("Product SKU is required")
  if (!data.productName?.trim()) errors.push("Product name is required")
  if (!data.productDescription?.trim()) errors.push("Product description is required")

  // Validate product images upload status
  if (data.productImages && data.productImages.length > 0) {
    const uploadingImages = data.productImages.filter(
      img => img.upload_status === 'uploading' || img.upload_status === 'pending'
    )
    if (uploadingImages.length > 0) {
      errors.push('Please wait for all images to finish uploading')
    }

    const failedImages = data.productImages.filter(
      img => img.upload_status === 'error'
    )
    if (failedImages.length > 0) {
      errors.push('Some images failed to upload. Please retry or remove them.')
    }

    // Check if images have been uploaded with file_id (when onFileUpload is used)
    const imagesWithFiles = data.productImages.filter(img => img.file)
    const missingFileIds = imagesWithFiles.filter(
      img => img.file && !img.file_id && img.upload_status !== 'error'
    )
    if (missingFileIds.length > 0) {
      errors.push('All images must be uploaded before proceeding')
    }
  }

  // Validate product video
  if (data.productVideo) {
    if (data.productVideo.type === 'file') {
      // Validate file upload status
      if (data.productVideo.upload_status === 'uploading' || data.productVideo.upload_status === 'pending') {
        errors.push('Please wait for video to finish uploading')
      }
      if (data.productVideo.upload_status === 'error') {
        errors.push('Video upload failed. Please retry or remove it.')
      }
      // Check if file has been uploaded with file_id
      if (data.productVideo.file && !data.productVideo.file_id && data.productVideo.upload_status !== 'error') {
        errors.push('Video must be uploaded before proceeding')
      }
    } else if (data.productVideo.type === 'url' && data.productVideo.url) {
      // Validate URL format
      const urlPattern = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/|dailymotion\.com\/video\/)/i
      if (!urlPattern.test(data.productVideo.url)) {
        errors.push("Please enter a valid video URL (YouTube, Vimeo, Dailymotion)")
      }
    }
  }

  // Legacy video URL validation (backward compatibility)
  if (data.videoUrl && data.videoUrl.trim()) {
    const urlPattern = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/|dailymotion\.com\/video\/)/i
    if (!urlPattern.test(data.videoUrl)) {
      errors.push("Please enter a valid video URL (YouTube, Vimeo, Dailymotion)")
    }
  }

  return errors
}

// Classification validation
export function validateClassification(data: ClassificationData): string[] {
  const errors: string[] = []
  if (!data.categoryPath || data.categoryPath.length === 0) {
    errors.push("Product category is required")
  }
  if (!data.brandId || !data.brandName) {
    errors.push("Brand is required")
  }

  // Validate license file upload status
  if (data.licenseFile) {
    if (data.licenseUploadStatus === 'uploading') {
      errors.push('Please wait for license file to finish uploading')
    }
    if (data.licenseUploadStatus === 'error') {
      errors.push('License file upload failed. Please retry or remove it.')
    }
    // Check if file has been uploaded with file_id
    if (!data.licenseFileId && data.licenseUploadStatus !== 'error') {
      errors.push('License file must be uploaded before proceeding')
    }
  }

  return errors
}

// Sales Info validation
export function validateSalesInfo(data: SalesInfoData): string[] {
  const errors: string[] = []

  // Validate minimum order
  if (!data.minOrder || data.minOrder < 1) {
    errors.push("Minimum order must be at least 1")
  }

  // If no variants, validate base pricing and stock
  if (!data.hasVariants) {
    if (!data.productPrice || data.productPrice <= 0) {
      errors.push("Regular price must be greater than 0")
    }
    if (!data.costPrice || data.costPrice <= 0) {
      errors.push("Cost price must be greater than 0")
    }
    if (data.salePrice && data.productPrice && data.salePrice > data.productPrice) {
      errors.push("Sale price cannot be higher than regular price")
    }
    
    // Validate stock quantity
    if (!data.totalStock || data.totalStock <= 0) {
      errors.push("Stock quantity must be greater than 0")
    }
  }

  // If variants are enabled, validate that variant options are configured
  if (data.hasVariants) {
    if (!data.variantOptions || data.variantOptions.length === 0) {
      errors.push("Please add at least one variant option (e.g., Color, Size)")
    } else {
      // Check if variant options have valid names and values
      const hasValidOptions = data.variantOptions.some(
        (option) => option.name.trim() && option.values.length > 0
      )
      if (!hasValidOptions) {
        errors.push("Please configure variant options with names and values")
      }

      // Validate variant combinations have stock
      if (data.variantCombinations && data.variantCombinations.length > 0) {
        const hasStocklessVariants = data.variantCombinations.some(
          (variant) => !variant.stock || variant.stock <= 0
        )
        if (hasStocklessVariants) {
          errors.push("All product variants must have stock quantity greater than 0")
        }
      }
    }
  }

  // Validate warehouse stock consistency (for non-variant products only)
  if (!data.hasVariants && data.warehouseStock && Object.keys(data.warehouseStock).length > 0) {
    const hasWarehouseStock = Object.values(data.warehouseStock).some(
      (stock) => (Number(stock) || 0) > 0
    )
    if (hasWarehouseStock) {
      const warehouseTotal = Object.values(data.warehouseStock).reduce(
        (sum, stock) => sum + (Number(stock) || 0),
        0
      )

      // Allow small rounding differences (within 1 unit)
      if (Math.abs(warehouseTotal - (data.totalStock || 0)) > 1) {
        errors.push(
          `Warehouse stock total (${warehouseTotal}) doesn't match total stock (${data.totalStock || 0})`
        )
      }
    }
  }

  // Validate individual warehouse stocks are not negative
  if (data.warehouseStock) {
    Object.entries(data.warehouseStock).forEach(([warehouse, stock]) => {
      const stockNum = Number(stock) || 0
      if (stockNum < 0) {
        errors.push(`Warehouse ${warehouse} cannot have negative stock`)
      }
    })
  }

  return errors
}

// Shipping validation
export function validateShipping(data: ShippingData): string[] {
  const errors: string[] = []

  // Validate package weight
  if (!data.packageDimensions?.weight || data.packageDimensions.weight <= 0) {
    errors.push("Package weight is required and must be greater than 0")
  }

  // Validate package dimensions
  if (!data.packageDimensions?.length || data.packageDimensions.length <= 0) {
    errors.push("Package length is required and must be greater than 0")
  }

  if (!data.packageDimensions?.width || data.packageDimensions.width <= 0) {
    errors.push("Package width is required and must be greater than 0")
  }

  if (!data.packageDimensions?.height || data.packageDimensions.height <= 0) {
    errors.push("Package height is required and must be greater than 0")
  }

  // Validate shipping companies - at least one must be enabled
  const enabledCompanies = data.shippingCompanies?.filter((company) => company.enabled) || []
  
  if (enabledCompanies.length === 0) {
    errors.push("At least one shipping company must be enabled")
  } else {
    // Check that enabled companies have at least one enabled service
    const hasEnabledServices = enabledCompanies.some(
      (company) => company.services && company.services.some((service) => service.enabled)
    )
    if (!hasEnabledServices) {
      errors.push("At least one shipping service must be enabled for selected companies")
    }
  }

  // Validate pre-order settings if enabled
  if (data.preOrderSettings?.enabled) {
    if (!data.preOrderSettings.processingDays || data.preOrderSettings.processingDays <= 0) {
      errors.push("Processing days is required when pre-order is enabled")
    }
  }

  return errors
}

// Step validation dispatcher
export function validateStep(stepNumber: number, formData: ProductData): string[] {
  switch (stepNumber) {
    case 1:
      return validateBasicInfo(formData.basicInfo)
    case 2:
      return validateClassification(formData.classification)
    case 3:
      return validateSalesInfo(formData.salesInfo)
    case 4:
      return validateShipping(formData.shipping)
    case 5:
      return [] // Review step has no validation
    default:
      return []
  }
}

// Final validation for submission (stricter than step validation)
export function validateForSubmission(data: BasicInfoData): string[] {
  const errors = validateBasicInfo(data)

  // Add image requirements for final submission
  if (!data.productImages || data.productImages.length === 0) {
    errors.push("At least one product image is required")
  } else {
    const hasMainImage = data.productImages.some((img) => img.isMain)
    if (!hasMainImage) {
      errors.push("Please select a main image")
    }
  }

  return errors
}

// Complete form validation for submission
export function validateAllSteps(formData: ProductData): Record<number, string[]> {
  const allErrors: Record<number, string[]> = {}

  // Step 1 uses strict validation for submission
  const step1Errors = validateForSubmission(formData.basicInfo)
  if (step1Errors.length > 0) {
    allErrors[1] = step1Errors
  }

  // Other steps use regular validation
  for (let i = 2; i <= 5; i++) {
    const errors = validateStep(i, formData)
    if (errors.length > 0) {
      allErrors[i] = errors
    }
  }

  return allErrors
}